import { supabase } from './supabaseClient';
import { addIncome, deleteIncome } from './finances';
import { todayISO } from './dates';

// Invoicing, and the one-way valve between it and Accounting.
//
// The rule the whole module is built around: **an invoice is a claim, income
// is money that arrived.** Only a Paid invoice may appear in the books, and
// it may appear exactly once. Everything below exists to keep that true even
// when a status is toggled by two people on two laptops.

export const INVOICE_STATUSES = ['Draft', 'Sent', 'Paid', 'Cancelled'];

// Statuses that mean money is still expected. Cancelled isn't owed and Paid
// isn't outstanding; a Draft hasn't been given to anyone yet, so it's listed
// separately rather than counted as owed.
export const OPEN_STATUSES = ['Sent'];

export const INVOICE_STATUS_TONE = {
  Draft: 'neutral',
  Sent: 'warning',
  Paid: 'success',
  Cancelled: 'muted',
};

export const PAYMENT_METHODS = ['Bank Transfer', 'Cash', 'Card'];

// How a discount is expressed. A percentage is what actually gets offered
// ("10% off"), but the flat amount exists because a rounded-down total
// ("call it J$16,000") is the other half of how haggling works, and deriving
// the percentage that produces it is arithmetic nobody should have to do.
export const DISCOUNT_TYPES = ['Percent', 'Amount'];
export const CHANNELS = ['Wholesale', 'In-Person', 'Website'];

// The business's own details, as printed at the top and bottom of every
// invoice. Kept here rather than in the database: there is one business, the
// details change about once a decade, and a settings table for a single row
// would be more machinery than the thing it stores.
export const BUSINESS = {
  name: 'Skin Fairy JA Co.',
  phones: ['+876-537-8094', '+876-298-6983'],
  emails: ['info@skinfairyja.com', 'tiffanyserena.skin@gmail.com'],
  website: 'www.skinfairyja.com',
  bank: {
    method: 'Bank Transfer',
    bankName: 'JMMB',
    accountName: 'Tiffany Maragh',
    accountNumber: '000 300 219 234',
    accountType: 'Chequeing',
  },
};

// ---------------------------------------------------------------------------
// Pure money — no Supabase past this point until the CRUD section
// ---------------------------------------------------------------------------

// One line's money. Kept as a function rather than read from the row's
// generated `line_total` column so the form can total a line that hasn't
// been saved yet, and so the on-screen total and the stored one are computed
// the same way.
export function lineTotal(item) {
  const price = Number(item?.unit_price) || 0;
  const qty = Number(item?.quantity) || 0;
  return price * qty;
}

// The lines added up, before any discount. Everything that says "Subtotal"
// on screen or on paper is this.
export function invoiceTotal(items) {
  return (items || []).reduce((sum, item) => sum + lineTotal(item), 0);
}

// Money rounds to the cent. A 10% discount on an odd subtotal otherwise
// produces a figure with a tail of binary noise, which then makes the printed
// total and the income entry disagree in the fourth decimal place.
function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

// What the discount takes off, in the invoice's own currency.
//
// Clamped to the subtotal: a flat discount larger than the invoice is a typo,
// and the sane reading of it is "pay nothing" rather than "we owe you money".
// An invoice has never been a refund and this is not the place to invent one.
export function discountAmount(invoice, items) {
  const subtotal = invoiceTotal(items ?? invoice?.invoice_items);
  const value = Number(invoice?.discount_value) || 0;
  if (value <= 0) return 0;
  const raw = invoice.discount_type === 'Percent' ? (subtotal * value) / 100 : value;
  return round2(Math.min(Math.max(raw, 0), subtotal));
}

// What is actually payable: the lines, less the discount. This — not
// invoiceTotal — is the figure the customer pays, the books receive, and the
// list shows.
export function invoiceNetTotal(invoice, items) {
  const lines = items ?? invoice?.invoice_items;
  return round2(invoiceTotal(lines) - discountAmount(invoice, lines));
}

// In JMD, whatever the invoice was written in — the figure the books want.
// Net of the discount, because a discount that didn't reduce what's owed
// wouldn't be a discount.
export function invoiceTotalJmd(invoice, items) {
  const rate = Number(invoice?.exchange_rate) || 1;
  return invoiceNetTotal(invoice, items ?? invoice?.invoice_items) * rate;
}

// Past its due date and still unpaid. Takes `today` rather than reading the
// clock, like the To-Do date helpers, so it's testable — and compares local
// date strings, never `new Date(str)` on a bare "YYYY-MM-DD".
export function isOverdue(invoice, today = todayISO()) {
  if (!invoice?.due_date) return false;
  if (invoice.status !== 'Sent') return false;
  return invoice.due_date < today;
}

// What's owed: Sent invoices only. A Draft is not a claim on anyone until
// it's been handed over, and counting it would overstate what's coming.
export function outstandingTotal(invoices) {
  return (invoices || [])
    .filter((inv) => OPEN_STATUSES.includes(inv.status))
    .reduce((sum, inv) => sum + invoiceTotalJmd(inv, inv.invoice_items), 0);
}

export function filterInvoices(invoices, { query = '', status = 'All' } = {}) {
  const q = query.trim().toLowerCase();
  return (invoices || []).filter((inv) => {
    if (status !== 'All' && inv.status !== status) return false;
    if (!q) return true;
    // Searched by what you'd remember: the customer, the number, or a thing
    // that was on it.
    return [
      inv.invoice_number,
      inv.bill_to_name,
      ...(inv.invoice_items || []).map((i) => i.description),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
}

// The income entry a paid invoice becomes.
//
// Pure and exported so the mapping is testable without a database, and so
// there is exactly one definition of how an invoice lands in the books.
// `amount_jmd` is deliberately absent: it's a generated column, and sending
// it would be rejected by Postgres.
export function invoiceToIncomeEntry(
  invoice,
  items,
  { paidOn = todayISO(), method = null, reference = null } = {}
) {
  const lines = items ?? invoice.invoice_items ?? [];
  return {
    entry_date: paidOn,
    // Names the invoice so the entry can be traced back from Accounting,
    // where the invoice itself isn't visible.
    description: `Invoice ${invoice.invoice_number} — ${invoice.bill_to_name}`,
    currency: invoice.currency || 'JMD',
    // Net of the discount: income is what arrived, and what arrived is what
    // the customer was actually asked to pay.
    amount: invoiceNetTotal(invoice, lines),
    exchange_rate: Number(invoice.exchange_rate) || 1,
    channel: invoice.channel || null,
    // The invoice number *is* the order number. Supplying it also stops the
    // 0004 trigger minting a second, unrelated IP-/WH- number for the sale.
    order_number: invoice.invoice_number,
    // How it was actually paid wins over what the invoice asked for — an
    // invoice offering bank transfer that gets settled in cash is normal.
    payment_method: method || invoice.payment_method || null,
    notes: reference ? `Payment reference: ${reference}` : null,
    created_by: invoice.created_by || null,
  };
}

// Which products move, and by how much, when an invoice is settled.
//
// Only catalogue-linked lines: a free-text line has no stock to adjust.
// `sign` is -1 when the invoice is paid and +1 when that is undone, so the
// same function describes both directions and they cannot disagree.
//
// Quantities for the same product are summed — an invoice listing a product
// on two lines must take off both.
export function stockAdjustments(items, products, sign = -1) {
  const wanted = new Map();
  for (const item of items || []) {
    if (!item.product_id) continue;
    const qty = Number(item.quantity) || 0;
    if (!qty) continue;
    wanted.set(item.product_id, (wanted.get(item.product_id) || 0) + qty);
  }

  const adjustments = [];
  for (const [productId, qty] of wanted) {
    const product = (products || []).find((p) => p.id === productId);
    if (!product) continue; // archived or deleted — nothing to move
    const current = Number(product.stock_qty) || 0;
    // stock_qty has a `check (stock_qty >= 0)`, so selling more than the
    // count says you had must land on zero rather than failing the write and
    // taking the whole payment down with it. The count was wrong, not the sale.
    const next = Math.max(0, current + sign * qty);
    if (next === current) continue;
    adjustments.push({ productId, name: product.name, from: current, to: next });
  }
  return adjustments;
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export async function listInvoices() {
  const { data, error } = await supabase
    .from('invoices')
    .select('*, invoice_items(*), stockist:stockists(*)')
    .order('issue_date', { ascending: false })
    .order('invoice_number', { ascending: false });
  if (error) throw error;
  // Line order is the order they were typed; Postgres gives no guarantee
  // without an explicit sort, and an invoice whose lines shuffle between
  // renders looks broken.
  return (data || []).map((inv) => ({
    ...inv,
    invoice_items: [...(inv.invoice_items || [])].sort((a, b) => a.sort_order - b.sort_order),
  }));
}

// Two writes, because the lines can't be sent until the invoice has an id.
// If the second one fails — the connection drops mid-save, which is exactly
// how this first went wrong — the first is undone rather than left behind: a
// numbered invoice with no lines on it looks like a real invoice for J$0, and
// it has already consumed a number from the sequence shown to customers.
export async function addInvoice(fields, items) {
  const { data, error } = await supabase.from('invoices').insert(fields).select().single();
  if (error) throw error;
  try {
    await replaceItems(data.id, items);
  } catch (err) {
    try {
      await supabase.from('invoices').delete().eq('id', data.id);
    } catch {
      // The rollback is best effort — if the network is gone it will fail too.
      // The original failure is the one worth reporting, so it is not replaced.
    }
    throw err;
  }
  return data;
}

// A stockist consignment: the stock physically leaves when it's sent, so it
// comes off your own count immediately — that's what makes "Stock currently
// out" a real number and stops the shelf reading higher than it is.
//
// Nothing is booked as income here. The consignment is inventory that happens
// to be elsewhere until a month's sales are reported and paid.
export async function createStockistInvoice(fields, items, products) {
  const invoice = await addInvoice({ ...fields, kind: 'Stockist' }, items);

  const adjustments = stockAdjustments(items, products, -1);
  const result = { invoice, adjustments, stockError: null };
  try {
    await applyAdjustments(adjustments);
    await supabase
      .from('invoices')
      .update({ stock_applied_at: new Date().toISOString() })
      .eq('id', invoice.id);
  } catch (err) {
    // Contained, like the direct invoice's stock step: a count that failed to
    // update must not lose the consignment record itself. The count is
    // maintained at the shelf anyway.
    result.stockError = err.message;
  }
  return result;
}

// Moves product stock by an arbitrary set of deltas — used when returns come
// back from a stockist and land on your shelf again. Built on the same
// stockAdjustments/applyAdjustments pair as everything else so clamping and
// the "skip archived products" rule can't diverge.
// Signed: positive puts units back on your shelf (a return, or a consignment
// corrected downward), negative sends more out.
//
// The two directions are computed separately and never for the same product
// twice — the caller nets per product first, because two adjustments derived
// from the same starting count would each overwrite the other.
export async function applyStockDeltas(deltas, products) {
  const toItems = (list) =>
    list.map((d) => ({ product_id: d.productId, quantity: Math.abs(d.quantity) }));

  const adjustments = [
    ...stockAdjustments(toItems(deltas.filter((d) => d.quantity > 0)), products, +1),
    ...stockAdjustments(toItems(deltas.filter((d) => d.quantity < 0)), products, -1),
  ];
  await applyAdjustments(adjustments);
  return adjustments;
}

export async function updateInvoice(id, updates, items) {
  const { data, error } = await supabase
    .from('invoices')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  if (items) await replaceItems(id, items);
  return data;
}

// Lines are replaced wholesale rather than diffed. They have no identity worth
// preserving — nothing references an invoice line — and a diff here would be
// three code paths to get wrong for no gain.
async function replaceItems(invoiceId, items) {
  const { error: delError } = await supabase
    .from('invoice_items')
    .delete()
    .eq('invoice_id', invoiceId);
  if (delError) throw delError;

  const rows = (items || [])
    .filter((item) => item.description?.trim())
    .map((item, index) => ({
      invoice_id: invoiceId,
      sort_order: index,
      item_date: item.item_date || null,
      description: item.description.trim(),
      product_id: item.product_id || null,
      unit_price: Number(item.unit_price) || 0,
      quantity: Number(item.quantity) || 0,
      // line_total is generated by Postgres — never sent.
    }));

  if (rows.length === 0) return;
  const { error } = await supabase.from('invoice_items').insert(rows);
  if (error) throw error;
}

export async function deleteInvoice(id) {
  const { error } = await supabase.from('invoices').delete().eq('id', id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Paid → income, and back
// ---------------------------------------------------------------------------

// Marks an invoice paid: writes the income entry, takes the stock off, and
// records both on the invoice so neither can happen twice.
//
// Order matters. The income entry is written *first* and linked before stock
// moves, because money is the part that must not be lost — if the stock
// update fails, the sale is still in the books and the count can be fixed at
// the shelf, which is where it's maintained anyway. The reverse order would
// risk stock leaving with no record of the sale.
export async function markInvoicePaid(
  invoice,
  products,
  { paidOn = todayISO(), method = null, reference = null } = {}
) {
  const items = invoice.invoice_items || [];
  const result = { incomeEntry: null, adjustments: [], stockError: null };

  const updates = { status: 'Paid', paid_at: new Date().toISOString() };
  if (method) updates.payment_method = method;

  // Only when there isn't one already: this is what makes paying twice safe.
  if (!invoice.income_entry_id) {
    const entry = await addIncome(invoiceToIncomeEntry(invoice, items, { paidOn, method, reference }));
    result.incomeEntry = entry;
    updates.income_entry_id = entry.id;
  }

  if (!invoice.stock_applied_at) {
    const adjustments = stockAdjustments(items, products, -1);
    try {
      await applyAdjustments(adjustments);
      result.adjustments = adjustments;
      updates.stock_applied_at = new Date().toISOString();
    } catch (err) {
      // Contained on purpose: a stock write that fails must not roll back a
      // payment that really happened. Reported, not swallowed.
      result.stockError = err.message;
    }
  }

  // Checked, and loudly: if the income entry was written but this link fails
  // to save, the next attempt would see a null income_entry_id and write a
  // *second* entry. There are no transactions across PostgREST calls, so the
  // failure has to name the orphan rather than be swallowed.
  const { error } = await supabase.from('invoices').update(updates).eq('id', invoice.id);
  if (error) {
    throw new Error(
      result.incomeEntry
        ? `The payment was recorded in Accounting (entry ${result.incomeEntry.id}) but the invoice could not be marked paid: ${error.message}. Check Income before marking it paid again, or it will be counted twice.`
        : `Couldn't update the invoice: ${error.message}`
    );
  }
  return result;
}

// Undoes the above when an invoice moves off Paid.
//
// It genuinely reverses rather than leaving the income behind: the entry was
// created by this app, not typed by hand, and income sitting in the books for
// an invoice that isn't paid is a worse error than the reversal. The invoice
// is left able to be paid again cleanly, because both stamps are cleared.
export async function unmarkInvoicePaid(invoice, products, nextStatus = 'Sent') {
  const result = { removedIncome: false, adjustments: [], stockError: null };
  const updates = { status: nextStatus, paid_at: null };

  if (invoice.income_entry_id) {
    await deleteIncome(invoice.income_entry_id);
    result.removedIncome = true;
    updates.income_entry_id = null;
  }

  if (invoice.stock_applied_at) {
    const adjustments = stockAdjustments(invoice.invoice_items || [], products, +1);
    try {
      await applyAdjustments(adjustments);
      result.adjustments = adjustments;
      updates.stock_applied_at = null;
    } catch (err) {
      result.stockError = err.message;
    }
  }

  const { error } = await supabase.from('invoices').update(updates).eq('id', invoice.id);
  // The income entry is already gone at this point, so a failure here leaves
  // the invoice claiming a link to a row that no longer exists. Say so — the
  // fix is to retry, which is safe (deleteIncome on a missing row is a no-op).
  if (error) throw new Error(`Couldn't update the invoice: ${error.message}`);
  return result;
}

async function applyAdjustments(adjustments) {
  for (const adj of adjustments) {
    const { error } = await supabase
      .from('products')
      .update({ stock_qty: adj.to })
      .eq('id', adj.productId);
    if (error) throw error;
  }
}

// The status change an invoice is actually asking for, so the page doesn't
// have to know which transitions have side effects.
export function statusChangeKind(from, to) {
  if (from === to) return 'none';
  if (to === 'Paid') return 'pay';
  if (from === 'Paid') return 'unpay';
  return 'plain';
}
